import { useCallback, useState, useRef } from "react";
import { KonvaArtStageRefs } from "../components/KonvaCanvas";

interface SerializedImageData {
  svgDataUrl: string;
  pngDataUrl: string;
  jpgDataUrl: string;
}

/**
 * Hook for serializing Konva canvas to SVG, PNG, and JPG formats
 */
export const useSerializeImage = (
  stageRef: React.RefObject<KonvaArtStageRefs>,
  panelSize: number
) => {
  const [userPng, setUserPng] = useState('');
  const lastSerializationTimeRef = useRef(Date.now());
  const IMAGE_SIZE = 512;

  /**
   * Serializes the current canvas state to SVG, PNG, and JPG formats
   * @returns Promise with the serialized image data
   */
  const serializeImage = useCallback(async (): Promise<SerializedImageData> => {
    return new Promise<SerializedImageData>(async (resolve) => {
      const drawStage = stageRef.current?.drawStageRef.current;
      if (!drawStage) {
        console.error('Cannot serialize - stage ref is null');
        return;
      }

      // Get transformer and temporarily hide it
      const transformer = drawStage.find('Transformer')[0];
      const wasTransformerVisible = transformer?.visible();
      
      // Hide transformer before capturing
      if (transformer) {
        transformer.visible(false);
        drawStage.batchDraw();
      }

      // Get SVG data from Konva stage
      const svgData = drawStage.toDataURL({ pixelRatio: 2 });
      
      // Restore transformer visibility
      if (transformer && wasTransformerVisible) {
        transformer.visible(true);
        drawStage.batchDraw();
      }
      
      // Create an image from the SVG data
      const img = document.createElement('img');
      img.src = svgData;

      img.onload = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = IMAGE_SIZE;
        canvas.height = IMAGE_SIZE;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }
        
        const pngDataUrl = canvas.toDataURL('image/png');
        const jpgDataUrl = canvas.toDataURL('image/jpeg');
        
        resolve({ svgDataUrl: svgData, pngDataUrl, jpgDataUrl });
      };
    });
  }, [panelSize, stageRef]);

  /**
   * Stores the serialized image data in state
   */
  const storeImage = useCallback(({ svgDataUrl, pngDataUrl, jpgDataUrl }: SerializedImageData) => {
    if (pngDataUrl !== userPng) {
      setUserPng(pngDataUrl);
      lastSerializationTimeRef.current = Date.now();
      // console.log('SVG:', svgDataUrl);
      // console.log('PNG:', pngDataUrl);
    }
  }, [userPng]);

  return { 
    serializeImage, 
    storeImage,
    userPng, 
    setUserPng,
    lastSerializationTimeRef
  };
};
